'use client'

import { useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { cn, Price } from '@segway/core'
import { useClickAway } from 'ahooks'

import { Carousel, CarouselItem, Dialog, InStock, ToolTipIcon } from '@/components'
import { Link } from '@/i18n/navigation'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui'
import { Button, Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/ui'

import { BundleProducts, useProduct } from '../../ProductProvider'

interface DetailPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: BundleProducts | null
}

const DetailPopup: React.FC<DetailPopupProps> = ({ open, onOpenChange, product }) => {
  const { responsive } = useProduct()
  const t = useTranslations('Common')
  const modalRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  useClickAway(() => {
    setIsOpen(false)
  }, modalRef)

  return (
    <>
      {responsive && !responsive.xl && (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="border-none">
            <DrawerHeader className="absolute right-0 top-0 z-10 border-none">
              <DrawerTitle>
                <span></span>
              </DrawerTitle>
            </DrawerHeader>
            <div className="flex flex-col overflow-hidden">
              <div className="relative w-full overflow-y-visible">
                <Carousel
                  autoPlay={false}
                  showArrows={false}
                  showIndicators={!!product?.images?.length}
                  indicatorsContainerClassName="bottom-[14px] gap-1"
                  indicatorsActiveItemClassName="bg-black/40 h-[6px]"
                  indicatorsItemClassName="bg-black/15 h-[6px] w-[6px]">
                  {!!product?.images?.length ? (
                    product?.images.map((item, index) => {
                      return (
                        <CarouselItem
                          key={index}
                          className="relative overflow-hidden rounded-tl-lg rounded-tr-lg pb-[100%]">
                          <Image src={item} alt="product" fill style={{ objectFit: 'cover' }} />
                        </CarouselItem>
                      )
                    })
                  ) : (
                    <CarouselItem className="item-center flex h-[310px] justify-center rounded-lg px-4" />
                  )}
                </Carousel>
                <div className="px-5">
                  <h3 className="mt-4 line-clamp-2 font-miSansSemiBold520 text-xl">
                    {product?.name}
                  </h3>
                  <div className="mt-5 flex items-center justify-between">
                    {product?.price && (
                      // <PriceRange
                      //   priceRange={product.price}
                      //   currencyStyle="text-sm text-primary"
                      //   textStyle="text-xl text-primary"
                      // />
                      <Price
                        price={{ value: product?.price }}
                        currencyStyle="text-sm text-primary"
                        textStyle="text-xl text-primary"
                        fractionStyle="text-xl text-primary"
                      />
                    )}
                    <div className="relative flex items-center gap-1" ref={modalRef}>
                      <span className="font-miSansMedium380 text-base">MSRP</span>
                      <span
                        onClick={() => {
                          setIsOpen(true)
                        }}>
                        <ToolTipIcon />
                      </span>
                      <div
                        className={cn(
                          'text-primary-foreground absolute left-[-100px] top-[-130px] z-50 mr-2 overflow-hidden rounded-md border border-gray-2 bg-white p-4 px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95',
                          isOpen ? 'block' : 'hidden',
                        )}>
                        <span>{t('msrp_content')}</span>
                      </div>
                    </div>
                  </div>
                  <p
                    className={cn(
                      'mt-4 flex items-center gap-2 text-base text-gray-8',
                      product?.isSaleable ? '' : 'opacity-40',
                    )}>
                    <InStock width={18} height={18} />
                    {product?.isSaleable ? 'In Stock' : 'Out of stock'}
                  </p>
                  {product?.description && (
                    <>
                      <div className="my-4 h-[1px] bg-gray-2"></div>
                      <p
                        className="font-miSansMedium380 text-base text-gray-5"
                        dangerouslySetInnerHTML={{ __html: product?.description || '' }}></p>
                    </>
                  )}
                </div>
              </div>
              <div className="mb-5 mt-4 px-5">
                <Link href={`/${product?.url_key}`}>
                  <Button className="h-[44px] w-full text-base">Learn More</Button>
                </Link>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {responsive && responsive.xl && (
        <Dialog
          open={open}
          onOpenChange={onOpenChange}
          containerStyle="max-w-[1200px] w-full p-0 overflow-hidden">
          <div className="flex max-h-[80vh] flex-col gap-9 overflow-hidden">
            <div className="relative max-h-[620px] overflow-y-auto xl:pr-[5px]">
              <div className="flex">
                <div>
                  <Carousel
                    autoPlay={false}
                    showIndicators={!!product?.images?.length}
                    showArrows={product?.images && product?.images.length > 2}
                    indicatorsContainerClassName="bottom-[40px]"
                    indicatorsActiveItemClassName="bg-black/40"
                    arrowLeftContainerClassName="left-[3%]"
                    arrowRightContainerClassName="right-[3%]"
                    indicatorsItemClassName="bg-black/15"
                    uiCarouselClassName="h-[600px] w-[600px]">
                    {!!product?.images?.length ? (
                      product?.images.map((item, index) => {
                        return (
                          <CarouselItem key={index}>
                            <div className="relative h-[600px] w-[600px]">
                              <Image src={item} alt="product" fill style={{ objectFit: 'cover' }} />
                            </div>
                          </CarouselItem>
                        )
                      })
                    ) : (
                      <CarouselItem className="flex h-[310px] justify-center rounded-lg" />
                    )}
                  </Carousel>
                </div>

                <div className="flex-1 p-7">
                  <h3 className="mt-[55px] line-clamp-2 font-miSansSemiBold520 text-pc-4xl leading-[1.6]">
                    {product?.name}
                  </h3>
                  <div className="mt-[20px] flex items-center justify-between">
                    <Price
                      price={{ value: product?.price || 0 }}
                      currencyStyle="text-lg"
                      textStyle="text-4xl"
                      fractionStyle="text-4xl "
                    />

                    <div className="flex items-center gap-1">
                      <span className="font-miSansMedium380 text-[14px] text-gray-6">MSRP</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <ToolTipIcon color="#86868B" />
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            className="max-w-[300px] rounded-md border border-gray-2 bg-white p-4 text-sm text-black shadow-[0px_8px_8px_-8px_rgba(0,0,0,0.1)]">
                            <p>{t('msrp_content')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <p
                    className={cn(
                      'mt-7 flex items-center gap-2 text-base',
                      product?.isSaleable ? '' : 'opacity-40',
                    )}>
                    <InStock width={20} height={20} />
                    {product?.isSaleable ? 'In Stock' : 'Out of stock'}
                  </p>

                  {product?.description && (
                    <>
                      <div className="my-9 h-[1px] bg-gray-2"></div>
                      <p
                        className="font-miSansMedium380 text-base text-gray-5"
                        dangerouslySetInnerHTML={{ __html: product?.description || '' }}></p>
                    </>
                  )}
                  <div className="mt-9">
                    <Link href={`/${product?.url_key}`}>
                      <Button className="h-[56px] w-full font-miSansMedium380 text-2xl">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}

export default DetailPopup
